// Small hook + module cache for the admin-configurable page visibility map
// AND custom labels (renaming). Backend:
//   GET  /api/site-settings/page-visibility → {visibility, labels}
//   PUT  /api/site-settings/page-visibility  (partial: send `visibility` and/or `labels`)
//
// Admins bypass hidden checks in the UI (nav still shows links so they can
// preview); non-admins get filtered-out nav + a redirect from hidden routes.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DEFAULT_VIS = { home: true, pathways: true, glossary: true, bookmarks: true, tools_and_metrics: true };
const DEFAULT_LABELS = {
  home: "Home",
  pathways: "Pathways",
  glossary: "Glossary",
  bookmarks: "Bookmarks",
  tools_and_metrics: "Tools & Metrics",
};

const DEFAULT_ORDER = ["home", "pathways", "glossary", "bookmarks", "tools_and_metrics"];

let cache = null;
let pending = null;
const subs = new Set();

export function invalidatePageVisibility() {
  cache = null;
  pending = null;
}

function normalise(data) {
  const rawOrder = Array.isArray(data?.order) ? data.order : [];
  const seen = [];
  for (const k of rawOrder) if (DEFAULT_ORDER.includes(k) && !seen.includes(k)) seen.push(k);
  for (const k of DEFAULT_ORDER) if (!seen.includes(k)) seen.push(k);
  return {
    visibility: { ...DEFAULT_VIS, ...(data?.visibility || {}) },
    labels: { ...DEFAULT_LABELS, ...(data?.labels || {}) },
    order: seen,
  };
}

async function fetchVisibility() {
  if (cache) return cache;
  if (pending) return pending;
  pending = api.get("/site-settings/page-visibility")
    .then(r => { cache = normalise(r.data); subs.forEach(fn => fn(cache)); return cache; })
    .catch(() => { cache = normalise({}); subs.forEach(fn => fn(cache)); return cache; })
    .finally(() => { pending = null; });
  return pending;
}

// Backwards-compat hook: returns the raw visibility map so existing callers
// (Layout, ToolsAndMetrics) keep working unchanged.
export function usePageVisibility() {
  const bundle = usePageVisibilityBundle();
  return bundle.visibility;
}

// Full hook — returns {visibility, labels}.
export function usePageVisibilityBundle() {
  const [b, setB] = useState(cache || normalise({}));
  useEffect(() => {
    let alive = true;
    fetchVisibility().then(x => { if (alive) setB(x); });
    const sub = (x) => alive && setB(x);
    subs.add(sub);
    return () => { alive = false; subs.delete(sub); };
  }, []);
  return b;
}

export async function saveVisibility(visibility) {
  const r = await api.put("/site-settings/page-visibility", { visibility });
  cache = normalise(r.data);
  subs.forEach(fn => fn(cache));
  return cache;
}

export async function saveLabels(labels) {
  const r = await api.put("/site-settings/page-visibility", { labels });
  cache = normalise(r.data);
  subs.forEach(fn => fn(cache));
  return cache;
}

export async function saveOrder(order) {
  const r = await api.put("/site-settings/page-visibility", { order });
  cache = normalise(r.data);
  subs.forEach(fn => fn(cache));
  return cache;
}
